import { createTheme, ThemeOptions } from '@mui/material';
import { alpha } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import COLORS from '../constants/colors';

const palette = {
  primary: {
    main: COLORS.PRIMARY_BLUE,
  },
  secondary: {
    main: COLORS.SECONDARY_GREEN,
  },
};
/**
 * Shared form-control language. Every screen mixed MUI defaults with one-off
 * `sx` tweaks, so inputs never matched each other; these overrides settle
 * radius, border colour and focus behaviour in one place for the whole app.
 */
const FIELD = {
  radius: '8px',
  border: alpha(COLORS.MAIN_DARK, 0.18),
  borderHover: alpha(COLORS.MAIN_DARK, 0.34),
  /** Disabled inputs on the admin screens are read, not just skipped, so they
   *  stay legible instead of using MUI's near-invisible default. */
  disabledText: alpha(COLORS.MAIN_DARK, 0.62),
  disabledSurface: alpha(COLORS.MAIN_DARK, 0.02),
};

const components: ThemeOptions['components'] = {
  MuiAppBar: {
    styleOverrides: {
      colorPrimary: {
        backgroundColor: '#fcfcfb',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '8px',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      sizeSmall: {
        padding: '5px 12px',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: FIELD.radius,
        backgroundColor: COLORS.WHITE,
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: FIELD.border,
          transition: 'border-color 150ms ease',
        },
        '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
          borderColor: FIELD.borderHover,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: COLORS.PRIMARY_BLUE,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: COLORS.RED,
        },
        '&.Mui-disabled': {
          backgroundColor: FIELD.disabledSurface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(COLORS.MAIN_DARK, 0.12),
          },
        },
      },
      input: {
        '&.Mui-disabled': {
          WebkitTextFillColor: FIELD.disabledText,
          color: FIELD.disabledText,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        color: COLORS.DARK_GREY,
        '&.Mui-focused': {
          color: COLORS.PRIMARY_BLUE,
        },
        '&.Mui-error': {
          color: COLORS.RED,
        },
        '&.Mui-disabled': {
          color: alpha(COLORS.MAIN_DARK, 0.45),
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: '0.75rem',
        marginTop: '4px',
        '&.Mui-error': {
          color: COLORS.RED,
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        fontSize: '0.875rem',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        borderRadius: '6px',
        margin: '0 6px',
        '&.Mui-selected': {
          backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.1),
          '&:hover': {
            backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.16),
          },
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        paddingTop: '6px',
        paddingBottom: '6px',
      },
      option: {
        fontSize: '0.875rem',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        fontWeight: 600,
      },
      sizeSmall: {
        height: '24px',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '0.75rem',
        borderRadius: '6px',
        padding: '6px 10px',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '12px',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.0625rem',
        fontWeight: 600,
        color: COLORS.MAIN_DARK,
        padding: '16px 24px',
        borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '20px 24px',
        // MUI zeroes this when a title precedes the content, which collapsed
        // the first field against the title's new rule.
        '.MuiDialogTitle-root + &': {
          paddingTop: '20px',
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '14px 24px',
        gap: '8px',
        // Content scrolls under this bar, so it needs its own edge.
        borderTop: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
      },
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        color: COLORS.LIGHT_GRAY,
        marginBottom: '4px',
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      /**
       * react-phone-number-input ships unstyled markup, so the country selector
       * did not match the field beside it: no border, a stretched box and a flag
       * rendered at text size. The 16px offset lines its box up with the
       * TextField's, which every caller renders with margin="normal".
       * The country list itself is a native <select>, so the popup is drawn by
       * the OS and cannot be styled from here.
       */
      '.PhoneInput': {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        '--PhoneInputCountrySelectArrow-color': alpha(COLORS.MAIN_DARK, 0.55),
        '--PhoneInputCountrySelectArrow-opacity': '1',
        '--PhoneInput-color--focus': COLORS.PRIMARY_BLUE,
      },
      '.PhoneInputCountry': {
        position: 'relative',
        boxSizing: 'border-box',
        // Matches MuiFormControl margin="normal" (16px), so both boxes align.
        marginTop: '16px',
        marginRight: 0,
        height: '56px',
        minWidth: '78px',
        padding: '0 10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        borderRadius: FIELD.radius,
        border: `1px solid ${FIELD.border}`,
        backgroundColor: COLORS.WHITE,
        cursor: 'pointer',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          borderColor: FIELD.borderHover,
        },
        '&:focus-within': {
          borderColor: COLORS.PRIMARY_BLUE,
          boxShadow: `0 0 0 1px ${COLORS.PRIMARY_BLUE}`,
        },
      },
      '.PhoneInputCountryIcon': {
        width: '30px',
        height: '22px',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: 'none',
        border: `1px solid ${alpha(COLORS.MAIN_DARK, 0.12)}`,
      },
      '.PhoneInputCountryIcon--border': {
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
      '.PhoneInputCountryIconImg': {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
      '.PhoneInputCountrySelectArrow': {
        width: '7px',
        height: '7px',
        marginLeft: 0,
        opacity: 1,
        borderStyle: 'solid',
        borderWidth: '0 1.5px 1.5px 0',
        borderColor: alpha(COLORS.MAIN_DARK, 0.55),
        borderTopColor: 'transparent',
        borderLeftColor: 'transparent',
      },
      '.PhoneInputCountrySelect:focus + .PhoneInputCountryIcon + .PhoneInputCountrySelectArrow': {
        borderColor: COLORS.PRIMARY_BLUE,
        borderTopColor: 'transparent',
        borderLeftColor: 'transparent',
      },
      '.PhoneInputInput': {
        minWidth: 0,
      },
    },
  },
};

const rtlTypography = {
  typography: {
    fontFamily: '"Cairo" ,sans-serif',
    bodySmallAr: {
      fontFamily: "'Cairo', sans-serif",
      fontWeight: 400,
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      lineHeight: '20px',
      textTransform: 'capitalize',
    },
    largeDisplayH: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(2rem,3vw + 1rem,2.5rem)',
      fontWeight: 500,
      lineHeight: '56px',
    },
    cairoHeader1B: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(2rem,3vw + 1rem,2.5rem)',
      fontWeight: 600,
      lineHeight: '75px',
    },
    cairoHeader3: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: '30px',
      fontWeight: 500,
      lineHeight: '40px',
    },
    cairoHeader3Custom: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(1.5rem,2vw + 1rem,1.875rem)',
      fontWeight: 500,
      lineHeight: '40px',
    },
    cairoHeader2: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: 'clamp(2rem,4vw + 1rem,2.5rem)',
      fontWeight: 500,
      lineHeight: '48px',
    },
    cairoR: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      fontWeight: 400,
      lineHeight: '20px',
    },
    cairoBody: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: 'clamp(1rem,6vw + 1rem,1.125rem)',
      fontWeight: 400,
      lineHeight: '28px',
    },
    cairoButton: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: 'clamp(1rem,1vw + 1rem,1.25rem)',
      fontWeight: 600,
      lineHeight: '32px',
    },
    cairoButtonR: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: 'clamp(1rem,1vw + 1rem,1.25rem)',
      fontWeight: 400,
      lineHeight: '28px',
    },
    cairoBold: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '20px',
    },
    cairoM: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '24px',
    },
    smallDisplayH: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'clamp(2rem,3vw + 1rem,2.75rem)',
      lineHeight: '48px',
    },
    bodyLarge: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 'clamp(1rem,6vw + 1rem,1.125rem)',
      lineHeight: '28px',
    },
    // bodyLargeM: {
    //   fontFamily: 'Cairo',
    //   fontStyle: 'normal',
    //   fontWeight: 500,
    //   fontSize: 'clamp(1rem,6vw + 1rem,1.125rem)',
    //   lineHeight: '28px',
    // },
    bodyMedium: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '24px',
    },
    bodyMediumB: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: '24px',
    },
    bodyMediumM: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: '24px',
    },
    bodySmall: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      lineHeight: '24px',
    },
    cairoBodySmall: {
      fontFamily: 'Cairo',
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      fontWeight: 600,
      lineHeight: '20px',
    },
    h1: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(2rem,3vw + 1rem,2.5rem)',
      fontWeight: 500,
      lineHeight: '48px',
    },
    h2: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(1.5rem,2vw + 1rem,2rem)',
      fontWeight: 500,
      lineHeight: '40px',
    },
    h3: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(1.25rem,3vw + 1rem,1.75rem)',
      fontWeight: 600,
      lineHeight: '36px',
    },
    h4: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: 'clamp(1rem,0.5vw + 1rem,1.25rem)',
      fontWeight: 600,
      lineHeight: '28px',
    },
    h6: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'clamp(1rem,0.5vw + 1rem,1.25rem)',
      lineHeight: '28px',
    },
    body2: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      lineHeight: '20px',
    },
    cairoR16: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '20px',
    },
    cairoH4: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: 'clamp(1rem,0.5vw + 1rem,1.25rem)',
      lineHeight: '28px',
    },
    cairoH5: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'clamp(1rem,1vw + 1rem,1.5rem)',
      lineHeight: '32px',
    },
    cairoBodyB: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: '20px',
    },
    cairoBodyMB: {
      fontFamily: 'Cairo',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: 'clamp(1rem,0.5vw + 1rem,1.25rem)',
      lineHeight: '28px',
    },
  },
};

const ltrTypography = {
  typography: {
    fontFamily: "'Inter', sans-serif",
    cairoM: {
      fontFamily: '"Cairo" ,sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '24px',
    },
    smallDisplayH: {
      fontFamily: "'Inter', sans-serif",
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'clamp(1.25rem,1.25vw + 1rem,2.75rem)',
      lineHeight: '48px',
    },
    largeDisplayH: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(2rem,3vw + 1rem,2.5rem)',
      fontWeight: 500,
      lineHeight: '56px',
    },
    bodyLarge: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(1rem,0.2vw + 1rem,1.13rem)',
      fontWeight: 400,
      lineHeight: '28px',
    },
    // bodyLargeM: {
    //   fontFamily: "'Inter', sans-serif",
    //   fontStyle: 'normal',
    //   fontWeight: 500,
    //   fontSize: 'clamp(1rem,6vw + 1rem,1.125rem)',
    //   lineHeight: '28px',
    // },
    bodySmall: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      fontWeight: 400,
      lineHeight: '24px',
    },
    bodySmallB: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      fontWeight: 600,
      lineHeight: '20px',
    },
    bodySmallM: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      fontWeight: 500,
      lineHeight: '20px',
    },
    h1: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(2rem,3vw + 1rem,2.5rem)',
      fontWeight: 500,
      lineHeight: '48px',
    },
    h1B: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(1.375rem,2vw + 1rem,2.25rem)',
      fontWeight: 600,
    },
    h2: {
      fontFamily: "'Inter', sans-serif",
      fontSize: '2.25rem',
      fontWeight: 500,
      lineHeight: '44px',
    },
    h3: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(1.5rem,2vw + 1rem,2rem)',
      fontWeight: 500,
      lineHeight: '40px',
    },
    h4: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(1.25rem,3vw + 1rem,1.75rem)',
      fontWeight: 500,
      lineHeight: '36px',
    },
    h5: {
      fontFamily: "'Inter', sans-serif",
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'clamp(1rem,1vw + 1rem,1.5rem)',
      lineHeight: '32px',
    },
    h6: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(1rem,0.5vw + 1rem,1.25rem)',
      fontWeight: 500,
      lineHeight: '28px',
    },
    bodyMedium: {
      fontFamily: "'Inter', sans-serif",
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '24px',
    },
    bodyMediumB: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: '24px',
    },
    body2: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 'clamp(0.5rem,1vw + 1rem,0.875rem)',
      lineHeight: '20px',
    },
  },
};

const ltrTheme = createTheme({
  palette,
  components,
  direction: 'ltr',
  ...ltrTypography,
  spacing: 4,
} as ThemeOptions);
const rtlTheme = createTheme({
  palette,
  components,
  direction: 'rtl',
  ...rtlTypography,
  spacing: 4,
} as ThemeOptions);

const cacheLtr = createCache({
  key: 'muiltr',
});

const cacheRtl = createCache({
  key: 'muirtl',
  // prefixer is the only stylis plugin by default, so when
  // overriding the plugins you need to include it explicitly
  // if you want to retain the auto-prefixing behavior.
  stylisPlugins: [prefixer, rtlPlugin],
});

export { ltrTheme, rtlTheme, cacheLtr, cacheRtl };
